import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import type {FC} from 'react';
import {useMemo} from 'react';
import type {MessageResponse} from 'src/api/ProactApiClient';
import {AssistantStopReasonEnum} from 'src/api/ProactApiClient';
import {
  isDatasetTool,
  isMemoryTool,
} from 'src/app/assistant/assistantToolLabels';
import {ChatChart} from 'src/app/assistant/Chart/ChatChart';
import {ChatTable} from 'src/app/assistant/Table/ChatTable';
import {isRateableMessage} from 'src/app/assistant/Feedback/messageFeedback';
import {AssistantMemoryCard} from 'src/app/assistant/Memory/AssistantMemoryCard';
import {Button} from 'src/design/atoms/Button';
import type {
  StreamDone,
  StreamItem,
} from 'src/pages/Modules/Assistant/shared/useAssistantChat';
import {ChatMessage} from './ChatMessage';
import {ToolCallRow} from './ToolCallRow';

export interface ChatTranscriptProps {
  conversationId: number;
  messages: MessageResponse[] | undefined;
  isLoading: boolean;
  pendingUserContent: string | null;
  streamItems: StreamItem[];
  activeTool: string | null;
  isStreaming: boolean;
  streamError: string | null;
  streamDone?: StreamDone | null;
  onSuggestion?: (content: string) => void;
  /** Sends "Continue" after a reply that stopped at the tool limit. */
  onContinue?: () => void;
  onOpenMemory?: () => void;
}

export const CONTINUE_MESSAGE = 'Continue';

const NOOP = () => {};

// The live done event wins until the refetched list carries the reply.
const lastReplyStopReason = (
  messages: MessageResponse[] | undefined,
  streamDone: StreamDone | null | undefined
): AssistantStopReasonEnum | undefined => {
  if (streamDone) return streamDone.stopReason;
  const last = messages?.[messages.length - 1];
  return last && last.role.toLowerCase() === 'assistant'
    ? last.stopReason
    : undefined;
};

// The memory event follows its tool_finished directly; the memory row then replaces the done row.
const replacedByMemoryRow = (items: StreamItem[], index: number): boolean => {
  const item = items[index];
  return (
    item.kind === 'tool' &&
    isMemoryTool(item.tool) &&
    !item.running &&
    !item.isError &&
    items[index + 1]?.kind === 'memory'
  );
};

const STARTER_SUGGESTIONS = [
  'How do I report a safety event?',
  'How do I assign training?',
  'What knowledge articles are available?',
];

// Query rows are numbered per reply: a user row restarts the count.
const queryOrdinalStarts = (messages: MessageResponse[]): number[] => {
  let count = 0;
  return messages.map(message => {
    if (message.role.toLowerCase() === 'user') count = 0;
    const start = count + 1;
    count += (message.toolCalls ?? []).filter(call =>
      isDatasetTool(call.name)
    ).length;
    return start;
  });
};

export const ChatTranscript: FC<ChatTranscriptProps> = ({
  conversationId,
  messages,
  isLoading,
  pendingUserContent,
  streamItems,
  activeTool,
  isStreaming,
  streamError,
  streamDone,
  onSuggestion,
  onContinue,
  onOpenMemory = NOOP,
}) => {
  const ordinalStarts = useMemo(
    () => queryOrdinalStarts(messages ?? []),
    [messages]
  );
  const hasContent =
    (messages && messages.length > 0) ||
    pendingUserContent !== null ||
    isStreaming ||
    Boolean(streamError);
  const showContinue =
    !isStreaming &&
    onContinue !== undefined &&
    lastReplyStopReason(messages, streamDone) === AssistantStopReasonEnum.ToolLimit;

  return (
    <Box
      p="md"
      style={{minHeight: '100%', display: 'flex', flexDirection: 'column'}}>
      {isLoading ? (
        <Center py="xl" style={{flex: 1}}>
          <Loader size="lg" />
        </Center>
      ) : !hasContent ? (
        <Center style={{flex: 1}}>
          <Stack align="center" gap="sm">
            <Text c="dimmed">
              Ask Scout about Proact or your organization’s procedures.
            </Text>
            {onSuggestion &&
              STARTER_SUGGESTIONS.map(question => (
                <Button
                  key={question}
                  variant="subtle"
                  onClick={() => onSuggestion(question)}>
                  {question}
                </Button>
              ))}
          </Stack>
        </Center>
      ) : (
        <Stack gap="lg" style={{flex: 1}}>
          {(messages ?? []).map((message, index) => (
            <ChatMessage
              key={message.id}
              role={message.role}
              content={message.content ?? ''}
              truncated={message.truncated}
              toolCalls={message.toolCalls}
              charts={message.charts}
              tables={message.tables}
              queryOrdinalStart={ordinalStarts[index]}
              feedback={message.feedback}
              conversationId={conversationId}
              messageId={message.id}
              rateable={isRateableMessage(message)}
              memories={message.memories}
              usedMemories={message.usedMemories}
              thinkHarder={message.thinkHarder}
              onOpenMemory={onOpenMemory}
            />
          ))}

          {pendingUserContent !== null && (
            <ChatMessage role="user" content={pendingUserContent} />
          )}

          {streamItems.map((item, index) => {
            switch (item.kind) {
              case 'text':
                return (
                  <ChatMessage
                    key={index}
                    role="assistant"
                    content={item.text}
                    streaming
                  />
                );
              case 'chart':
                return <ChatChart key={index} chart={item.chart} />;
              case 'table':
                return <ChatTable key={index} table={item.table} />;
              case 'memory':
                return (
                  <AssistantMemoryCard
                    key={index}
                    note={item.note}
                    action={item.action}
                    onManage={onOpenMemory}
                  />
                );
              default:
                if (replacedByMemoryRow(streamItems, index)) return null;
                return (
                  <ToolCallRow
                    key={index}
                    tool={item.tool}
                    running={item.running}
                    isError={item.isError}
                  />
                );
            }
          })}

          {isStreaming && !activeTool && (
            <Group gap="xs">
              <Loader size="xs" type="dots" color="gray" />
              <Text size="sm" c="dimmed">
                Thinking...
              </Text>
            </Group>
          )}

          {showContinue && (
            <Group gap="sm">
              <Text size="sm" c="dimmed">
                Scout reached its tool limit for this reply.
              </Text>
              <Button onClick={onContinue}>Continue</Button>
            </Group>
          )}

          {streamError && (
            <Alert color="red" title="Scout error">
              {streamError}
            </Alert>
          )}
        </Stack>
      )}
    </Box>
  );
};
